import React from "react";
import { useTable, useSortBy, usePagination, Column } from "react-table";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Badge } from "components/ui/badge";

interface RequestTableProps {
  title: string;

  data: any[];
  columns: Column<any>[];
}

const RequestTable: React.FC<RequestTableProps> = ({
  title,
  data,
  columns,
}) => {
  // Create a table instance using react-table hooks
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useSortBy,
      usePagination
    );

  const renderTableRows = () => {
    if (rows.length === 0) {
      return (
        <tr>
          <td
            colSpan={columns.length}
            className="px-6 py-8 text-center text-gray-500 bg-gray-50"
          >
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-gray-400 text-lg">—</span>
              </div>
              <span>No data available</span>
            </div>
          </td>
        </tr>
      );
    }

    return rows.map((row, index) => {
      prepareRow(row);
      return (
        <tr
          {...row.getRowProps()}
          key={index}
          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
        >
          {row.cells.map((cell, cellIndex) => {
            const cellProps = cell.getCellProps({
              style: {
                width: cell.column.width,
              },
            });

            const rowOverride = cell.row.original.row_override;
            // Only use row override if there's an override AND the cell value is null/undefined
            // But check the original data to see if it should be a boolean
            const originalValue = cell.row.original[cell.column.id];
            const shouldUseRowOverride =
              rowOverride != null &&
              cell.value == null &&
              typeof originalValue !== "boolean";

            if (shouldUseRowOverride) {
              return (
                <td
                  {...cellProps}
                  key={cellIndex}
                  className="px-6 py-4 text-sm text-gray-900"
                >
                  {rowOverride}
                </td>
              );
            }

            return (
              <td
                {...cellProps}
                key={cellIndex}
                className="px-6 py-4 text-sm text-gray-900"
              >
                {cell.render("Cell")}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-gray-900 flex items-center">
          {title}
          {data.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {data.length} {data.length === 1 ? "item" : "items"}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              {headerGroups.map((headerGroup, groupIndex) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={groupIndex}>
                  {headerGroup.headers.map((column, headerIndex) => (
                    <th
                      {...column.getHeaderProps({
                        style: { width: column.width },
                      })}
                      key={headerIndex}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.render("Header")}</span>
                        {(column as any).canSort && (
                          <div className="flex flex-col">
                            {(column as any).isSorted ? (
                              (column as any).isSortedDesc ? (
                                <ChevronDown className="h-3 w-3 text-gray-400" />
                              ) : (
                                <ChevronUp className="h-3 w-3 text-gray-400" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 text-gray-400" />
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody
              {...getTableBodyProps()}
              className="bg-white divide-y divide-gray-100"
            >
              {renderTableRows()}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RequestTable;
